// Pure helpers for location messages: pins, meeting points and live ETAs in
// chats. What a card and a preview say, the "Share location" sheet's checks
// (labels, meeting times), links that open a point in a maps app, the pinned
// meeting point, and `MapSlots`, which bounds how many mini maps are live
// (WebGL contexts) at once.
import type { Schemas } from "@10twenty/api";
import { dayKey, timeText } from "../history/history";
import { distanceText, type Units } from "../units";

export type MessageLocation = Schemas["MessageLocation"];
export type MessageEta = Schemas["MessageEta"];
export type LocationCreate = Schemas["LocationCreate"];
type Message = Schemas["Message"];
type Conversation = Schemas["Conversation"];

/** Longest label, in code points (as the server counts). */
export const LABEL_MAX = 80;
/** A meeting time may lie at most this far ahead (the server's limit). */
const MEET_AT_AHEAD_MS = 365 * 86_400_000;
/** The sheet refuses meeting times further back than this (the server allows a day: a typo, not a plan). */
const MEET_AT_SLACK_MS = 5 * 60_000;
/** An expected arrival this close (or past) reads "any minute". */
const ANY_MINUTE_MS = 60_000;

/** The trimmed label, or null when blank; `ok` is false when it is too long. */
export function checkLabel(text: string): { label: string | null; length: number; ok: boolean } {
  const trimmed = text.trim();
  const length = [...trimmed].length;
  return { label: trimmed === "" ? null : trimmed, length, ok: length <= LABEL_MAX };
}

/** Where a pin was dropped or a place is, for the sheet's "Drop a pin" and "Places". */
export interface Spot {
  lat: number;
  lon: number;
}

/** A point as `<input type="datetime-local">` writes it (local wall time), or null. */
export function meetAtFromInput(value: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number) as [number, number, number, number, number, number];
  const date = new Date(y, mo - 1, d, h, mi);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** A local wall time for `<input type="datetime-local">`. */
export function meetAtInput(ms: number): string {
  const date = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dayKey(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The sheet's suggested meeting time: at least 30 minutes ahead, on a quarter hour. */
export function defaultMeetAt(nowMs: number): number {
  const quarter = 15 * 60_000;
  return Math.ceil((nowMs + 30 * 60_000) / quarter) * quarter;
}

/** Why a meeting time can't be sent, or null when it can. */
export function meetAtError(iso: string, nowMs: number): string | null {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return "Pick a date and time.";
  if (ms < nowMs - MEET_AT_SLACK_MS) return "Pick a time that hasn't passed.";
  if (ms > nowMs + MEET_AT_AHEAD_MS) return "Pick a time within a year.";
  return null;
}

/** "14:30", "tomorrow 14:30" or "Sat 11 Oct, 14:30", in the viewer's zone. */
export function meetAtText(iso: string, nowMs: number, locale?: string): string {
  const ms = Date.parse(iso);
  const date = new Date(ms);
  const time = timeText(ms, locale);
  const today = new Date(nowMs);
  if (dayKey(date) === dayKey(today)) return `today ${time}`;
  const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  if (dayKey(date) === dayKey(tomorrow)) return `tomorrow ${time}`;
  const day = date.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
    ...(date.getFullYear() === today.getFullYear() ? {} : { year: "numeric" }),
  });
  return `${day}, ${time}`;
}

/** What a pin's point is called: its label, else by where it came from. */
export function placeName(location: Pick<MessageLocation, "label" | "source">): string {
  if (location.label) return location.label;
  return location.source === "current" ? "Current location" : "Dropped pin";
}

/**
 * One line for previews, quotes and drafts, as the server's push preview
 * says it: "Location" (": label"), "Meet at …", "ETA 14:35" / "Live ETA".
 */
export function locationText(message: Pick<Message, "location">, locale?: string): string {
  const location = message.location;
  if (!location) return "Location";
  switch (location.type) {
    case "pin":
      return location.label ? `Location: ${location.label}` : "Location";
    case "meeting_point":
      return `Meet at ${location.label ?? "a meeting point"}`;
    case "eta": {
      const eta = location.eta;
      if (eta?.state === "live" && eta.eta_at) return `ETA ${timeText(Date.parse(eta.eta_at), locale)}`;
      if (eta?.state === "arrived") return "Arrived";
      return eta?.state === "live" || !eta ? "Live ETA" : "ETA ended";
    }
  }
}

/** What a location message still being sent says. */
export function pendingLocationText(location: LocationCreate): string {
  switch (location.type) {
    case "pin":
      return location.point ? "My location" : location.label ? `Location: ${location.label}` : "Location";
    case "meeting_point":
      return `Meet at ${location.label ?? "a meeting point"}`;
    case "eta":
      return "Live ETA";
  }
}

export interface EtaView {
  /** "ETA 14:35", "Arriving any minute", "Arrived 14:32", "ETA ended" … */
  headline: string;
  /** "12 min · 8.4 km", "about", why it ended … or null. */
  detail: string | null;
  /** Still updating. */
  live: boolean;
}

/** How a live ETA card reads now. The sender's own position is never shown. */
export function etaView(eta: MessageEta, nowMs: number, units: Units = "metric", locale?: string): EtaView {
  switch (eta.state) {
    case "live": {
      if (!eta.eta_at) return { headline: "Live ETA", detail: "Waiting for their next location…", live: true };
      const at = Date.parse(eta.eta_at);
      const left = at - nowMs;
      const parts: string[] = [];
      if (left > ANY_MINUTE_MS) parts.push(durationText(left));
      if (eta.distance_m !== null) parts.push(distanceText(eta.distance_m, units));
      if (eta.approximate) parts.push("estimate");
      return {
        headline: left <= ANY_MINUTE_MS ? "Arriving any minute" : `ETA ${timeText(at, locale)}`,
        detail: parts.length > 0 ? parts.join(" · ") : null,
        live: true,
      };
    }
    case "arrived":
      return {
        headline: eta.eta_at ? `Arrived ${timeText(Date.parse(eta.eta_at), locale)}` : "Arrived",
        detail: null,
        live: false,
      };
    case "expired":
      return { headline: "ETA ended", detail: "Live ETAs end after 3 hours.", live: false };
    case "stopped":
      return { headline: "ETA ended", detail: "Stopped sharing.", live: false };
    case "paused":
      return { headline: "ETA ended", detail: "Location sharing with this chat was paused.", live: false };
  }
}

/** "1 h 5 min" or "12 min" (rounded up: arriving in under a minute reads "1 min"). */
export function durationText(ms: number): string {
  const minutes = Math.max(1, Math.ceil(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

/** The caller's newest still-live ETA among these messages, if any. */
export function ownLiveEta(messages: readonly Message[], selfId: string): Message | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]!;
    if (
      message.sender_id === selfId &&
      message.deleted_at === null &&
      message.location?.type === "eta" &&
      message.location.eta?.state === "live"
    ) {
      return message;
    }
  }
  return null;
}

/** Where the "Share location" sheet's point comes from. */
export type ShareSource =
  | { kind: "current"; point: Schemas["LiveLocation"] }
  | { kind: "dropped"; lat: number; lon: number }
  | { kind: "place"; placeId: string; name: string };

/**
 * The location to send, or why it can't be: an ETA needs a destination (a
 * pin or a place, never your own position), a meeting point a name (a
 * place's is its own) and a time only if one was picked.
 */
export function shareBody(
  source: ShareSource,
  type: LocationCreate["type"],
  labelText: string,
  meetAt: string | null,
  nowMs: number,
): { body: LocationCreate } | { error: string } {
  if (type === "eta" && source.kind === "current") return { error: "An ETA goes to a pin or a place." };
  // A place goes under its own name (the server copies it).
  const { label, ok } = checkLabel(source.kind === "place" ? "" : labelText);
  if (!ok) return { error: `Names can have up to ${LABEL_MAX} characters.` };
  if (type === "meeting_point" && label === null && source.kind !== "place") {
    return { error: "Name the meeting point." };
  }
  if (type === "meeting_point" && meetAt !== null) {
    const error = meetAtError(meetAt, nowMs);
    if (error) return { error };
  }
  const where =
    source.kind === "current"
      ? { point: source.point }
      : source.kind === "dropped"
        ? { lat: source.lat, lon: source.lon }
        : { place_id: source.placeId };
  return {
    body: {
      type,
      ...where,
      ...(label !== null ? { label } : {}),
      ...(type === "meeting_point" && meetAt !== null ? { meet_at: meetAt } : {}),
    },
  };
}

/** The conversation's pinned message when it carries a location (a meeting point, say) and has not disappeared. */
export function pinnedLocation(
  conversation: Pick<Conversation, "pinned">,
  nowMs: number,
): { message: Message; location: MessageLocation } | null {
  const message = conversation.pinned?.message;
  if (!message || message.deleted_at !== null || !message.location) return null;
  // A disappearing message goes from its expiry (chat.ts `isExpired`; chat.ts imports this module).
  if (message.expires_at && Date.parse(message.expires_at) <= nowMs) return null;
  return { message, location: message.location };
}

/** What a pin on the live map says: "Meeting point: …" or "Pinned in the chat: …". */
export function pinnedMapLabel(location: MessageLocation): string {
  const name = location.type === "pin" ? placeName(location) : (location.label ?? "");
  const kind = location.type === "meeting_point" ? "Meeting point" : "Pinned in the chat";
  return name ? `${kind}: ${name}` : kind;
}

/** An ETA to a pinned location: to the same point, under its name. */
export function etaToPinned(location: MessageLocation): LocationCreate {
  return { type: "eta", lat: location.lat, lon: location.lon, ...(location.label ? { label: location.label } : {}) };
}

/** Why a location send was refused, for a code the server gives (null: say what it said). */
export function locationErrorText(code: string | null | undefined): string | null {
  switch (code) {
    case "sharing_paused":
      return "Your location sharing is paused here, so your location can't be sent. Dropped pins and places still can.";
    case "approximate_sharing":
      return "You share only an approximate location here, so your exact location can't be sent.";
    case "event_not_live":
      return "Locations are shared in this chat only while the event is on.";
    default:
      return null;
  }
}

// --------------------------------------------------------------- maps apps

export type MapsPlatform = "apple" | "android" | "other";

/** Which maps app a link should open, from the browser's user agent. */
export function mapsPlatform(userAgent: string, maxTouchPoints = 0): MapsPlatform {
  // iPadOS reports a Mac; touch tells them apart.
  if (/iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) return "apple";
  if (/Android/.test(userAgent)) return "android";
  return "other";
}

/**
 * Opens the point in a maps app: Apple Maps on iOS, the `geo:` handler on
 * Android, OpenStreetMap elsewhere. Only followed when tapped.
 */
export function mapsUrl(point: Spot, label: string | null, platform: MapsPlatform): string {
  const lat = point.lat.toFixed(6);
  const lon = point.lon.toFixed(6);
  switch (platform) {
    case "apple":
      return `https://maps.apple.com/?ll=${lat},${lon}&q=${encodeURIComponent(label ?? "Pin")}`;
    case "android":
      return `geo:${lat},${lon}?q=${lat},${lon}${label ? `(${encodeURIComponent(label)})` : ""}`;
    case "other":
      return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;
  }
}

// ---------------------------------------------------------------- mini maps

/**
 * Hands out a bounded number of slots for live mini maps (each a WebGL
 * context; browsers cap them and drop the oldest). `request` grants a slot
 * at once when one is free, else when one is released, first come first
 * served; its return value releases the slot, or withdraws the request.
 */
export class MapSlots {
  private live = 0;
  private readonly waiting: (() => void)[] = [];

  constructor(readonly limit: number) {}

  request(grant: () => void): () => void {
    let state: "waiting" | "granted" | "done" = "waiting";
    const start = () => {
      state = "granted";
      this.live++;
      grant();
    };
    if (this.live < this.limit) start();
    else this.waiting.push(start);
    return () => {
      if (state === "granted") {
        this.live--;
        state = "done";
        this.next();
      } else if (state === "waiting") {
        state = "done";
        const at = this.waiting.indexOf(start);
        if (at >= 0) this.waiting.splice(at, 1);
      }
    };
  }

  /** Live maps now. */
  get size(): number {
    return this.live;
  }

  private next(): void {
    while (this.live < this.limit) {
      const start = this.waiting.shift();
      if (!start) return;
      start();
    }
  }
}

/** Snapshots of rendered mini maps, newest last, so each point renders once per page load. */
export class SnapshotCache {
  private readonly images = new Map<string, string>();

  constructor(readonly limit: number) {}

  static key(point: Spot): string {
    return `${point.lat.toFixed(5)},${point.lon.toFixed(5)}`;
  }

  get(point: Spot): string | undefined {
    const key = SnapshotCache.key(point);
    const image = this.images.get(key);
    if (image !== undefined) {
      this.images.delete(key);
      this.images.set(key, image);
    }
    return image;
  }

  set(point: Spot, image: string): void {
    const key = SnapshotCache.key(point);
    this.images.delete(key);
    this.images.set(key, image);
    while (this.images.size > this.limit) {
      const oldest = this.images.keys().next().value;
      if (oldest === undefined) break;
      this.images.delete(oldest);
    }
  }
}
